import React, { useMemo } from "react";
import { useAnimateInView } from "./useAnimateInView";
import useSeverityAsPriority, {
  SeverityFieldsEnum,
} from "../../General/useSeverityAsPriority";
import BigArrowImage from "../Assets/AggregationsPage/big-arrow.svg";
import SmallArrowImage from "../Assets/AggregationsPage/small-arrow.svg";
import BackgroundImage from "../Assets/AggregationsPage/bg.png";
import { formatNumber } from "@SeemplicityDev/shared";

interface AggregationsPageProps {
  data?: {
    raw_findings_count: number;
    aggregated_findings_count: number;
    critical_findings_count: number;
    aggregated_critical_findings_count: number;
  };
}

const AggregationsPage: React.FC<AggregationsPageProps> = ({ data }) => {
  const { ref, animateClassNames } = useAnimateInView();
  const { getSeverityField } = useSeverityAsPriority();

  const allFindingsDeductionPercentage = useMemo(() => {
    if (!data) {
      return 0;
    }
    return (
      100 -
      Math.round(
        (data?.aggregated_findings_count / data?.raw_findings_count) * 100
      )
    );
  }, [data?.aggregated_findings_count, data?.raw_findings_count]);

  const criticalFindingsDeductionPercentage = useMemo(() => {
    if (!data) {
      return 0;
    }
    return (
      100 -
      Math.round(
        (data?.aggregated_critical_findings_count /
          data?.critical_findings_count) *
          100
      )
    );
  }, [data?.aggregated_critical_findings_count, data?.critical_findings_count]);

  return (
    <div
      className={`eoy-page aggregations-page ${animateClassNames}`}
      ref={ref}
    >
      <img src={BackgroundImage} alt="Background" className="bg-image" />
      <h2 className="page-title">Efficient Prioritization</h2>
      <h3 className="page-subtitle">
        Smarter prioritization that transforms scale into focus and action
      </h3>
      <div className="deduction-container">
        <div className="percentage-item all-findings">
          <img src={BigArrowImage} alt="Big Arrow" className="big-arrow" />
          <span className="percentage-number">
            {allFindingsDeductionPercentage}%
          </span>
          <span className="percentage-text">Total Findings</span>
        </div>
        {criticalFindingsDeductionPercentage > 10 && (
          <div className="percentage-item critical-findings">
            <img
              src={SmallArrowImage}
              alt="Small Arrow"
              className="small-arrow"
            />
            <span className="percentage-number">
              {criticalFindingsDeductionPercentage}%
            </span>
            <div className="percentage-text">
              <span
                className="critical"
                style={{ color: getSeverityField(3, SeverityFieldsEnum.COLOR) }}
              >
                {getSeverityField(3, SeverityFieldsEnum.NAME)}
              </span>{" "}
              Findings
            </div>
          </div>
        )}
      </div>
      <div className="funnel-container">
        <div className="funnel" />
        <div className="counts">
          <div className="raw-count">
            <span className="count">
              {formatNumber(data?.raw_findings_count)}
            </span>
            <span className="text">Raw Findings</span>
          </div>
          <div className="aggregated-count">
            <span className="count">
              {formatNumber(data?.aggregated_findings_count)}
            </span>
            <span className="text">Findings</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AggregationsPage;
